/**
 * «Библиотека систем» — экран AK-773 (решение founder Р35).
 *
 * Что экран обещает: система защиты / питания / обработки почвы живёт ОТДЕЛЬНО
 * от техкарты. Её собирают один раз («Озимая пшеница — интенсив 2027»), а
 * дальше вставляют в любую техкарту и заменяют целиком — стоимость карты
 * пересчитывается. До этого набор операций защиты существовал только внутри
 * одной карты, и «взять защиту из интенсива» значило клонировать карту.
 *
 * Макет редактора (шаг 2) — `docs/superpowers/prototypes/season-planning-2027/07-system-editor.html`.
 *
 * Выбора хозяйства здесь СОЗНАТЕЛЬНО нет: библиотека сезонная, система
 * принадлежит компании и видна всем её хозяйствам. Появится разделение по
 * хозяйствам — хозяйство придёт из переключателя слева (AK-866 В-3: область —
 * из переключателя), своего выбора на экране не будет.
 */
import { useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCan } from '@/lib/auth/usePermissions';
import { listSeasonCards } from '@/lib/planning/api';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { formatPerHa, techCardErrorText, useCropNames } from '@/lib/planning/techCards';
import {
  MODULE_KIND_ORDER,
  MODULE_KIND_SHORT,
  MODULE_STATUS_TITLES,
  formatUsage,
  useSystemModules,
  type ModuleKind,
  type SystemModule,
} from '@/lib/planning/techCardModules';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { CreateSystemButton } from './CreateSystemButton';

export interface SystemsSearch {
  year?: number;
  kind?: ModuleKind;
  crop?: string;
}

/** Radix Select не принимает пустую строку как значение — нужен свой маркер «любой». */
const ANY = '__any__';

export function SystemsLibraryPage() {
  const search = useSearch({ strict: false }) as SystemsSearch;
  const navigate = useNavigate();

  const canRead = useCan('tech_cards', 'r');
  const canCreate = useCan('tech_cards', 'c');

  // Год в адресе необязателен: без него открываем текущий сезон — библиотека
  // это место, куда заходят «посмотреть свои системы», а не по ссылке на год.
  const cards = useQuery({
    queryKey: ['planning', 'season-cards', 'windowed'],
    queryFn: () => listSeasonCards(false),
    staleTime: 60_000,
    enabled: canRead,
  });
  const fallbackYear = cards.data?.find((c) => c.is_current)?.year ?? null;
  const year = search.year ?? fallbackYear ?? NaN;
  const season = useSeasonByYear(year);

  const kind = search.kind ?? null;
  const cropId = search.crop ?? null;
  const modules = useSystemModules({
    seasonId: canRead ? season.seasonId : null,
    kind,
    cropId,
  });
  const items = useMemo(() => modules.data ?? [], [modules.data]);
  const cropNames = useCropNames(items.map((m) => m.crop_id));

  // Культуры фильтра — те, что реально встречаются в библиотеке: выпадашка со
  // всем справочником заставила бы выбирать культуру, систем у которой нет.
  const cropOptions = useMemo(() => {
    const ids = [...new Set(items.map((m) => m.crop_id).filter((id): id is string => !!id))];
    return ids
      .map((id) => ({ id, name: cropNames.names[id] ?? 'культура без названия в справочнике' }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [items, cropNames.names]);

  const setSearch = (patch: Partial<SystemsSearch>) =>
    void navigate({
      to: '/planning/systems',
      search: { ...search, ...(Number.isFinite(year) ? { year } : {}), ...patch },
    });

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={
            Number.isFinite(year) ? `Библиотека систем · сезон ${year}` : 'Библиотека систем'
          }
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            { label: 'Библиотека систем' },
          ]}
          description="Система — мини-техкарта: собрал один раз, вставляешь в любую техкарту и заменяешь целиком. Стоимость карты пересчитывается при замене."
        />

        <Stack gap={4} className="mt-4">
          {!canRead && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Системы вам не открыты. Доступ к плановой себестоимости даёт администратор
                хозяйства — попросите роль с правом на техкарты.
              </CardContent>
            </Card>
          )}

          {canRead && (
            <>
              {(cards.isLoading || season.isLoading) && <Skeleton className="h-64 w-full" />}

              {season.missing && Number.isFinite(year) && <SeasonYearOutOfWindowCard year={year} />}
              {season.notOpened && (
                <SeasonNotStartedCard year={year} onOpened={() => undefined} />
              )}

              {season.seasonId && (
                <>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="w-56">
                      <Select
                        value={kind ?? ANY}
                        onValueChange={(v) =>
                          setSearch({ kind: v === ANY ? undefined : (v as ModuleKind) })
                        }
                      >
                        <SelectTrigger aria-label="Тип системы">
                          <SelectValue placeholder="Любой тип" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ANY}>Любой тип</SelectItem>
                          {MODULE_KIND_ORDER.map((k) => (
                            <SelectItem key={k} value={k}>
                              {MODULE_KIND_SHORT[k]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-64">
                      <Select
                        value={cropId ?? ANY}
                        onValueChange={(v) => setSearch({ crop: v === ANY ? undefined : v })}
                      >
                        <SelectTrigger aria-label="Культура">
                          <SelectValue placeholder="Любая культура" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ANY}>Любая культура</SelectItem>
                          {cropOptions.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      систем в сезоне: {items.length}
                    </span>
                    {canCreate && season.seasonId && (
                      <span className="ml-auto">
                        <CreateSystemButton
                          seasonId={season.seasonId}
                          onCreated={(moduleId) =>
                            void navigate({
                              to: '/planning/systems/$moduleId',
                              params: { moduleId },
                            })
                          }
                        />
                      </span>
                    )}
                  </div>

                  <SystemsTable
                    loading={modules.isLoading}
                    error={modules.isError ? techCardErrorText(modules.error) : null}
                    items={items}
                    cropNames={cropNames.names}
                    onOpen={(moduleId) =>
                      void navigate({ to: '/planning/systems/$moduleId', params: { moduleId } })
                    }
                  />
                </>
              )}
            </>
          )}
        </Stack>
      </div>
    </AppShell>
  );
}

export function SystemsTable({
  loading,
  error,
  items,
  cropNames,
  onOpen,
}: {
  loading: boolean;
  error: string | null;
  items: SystemModule[];
  cropNames: Record<string, string>;
  onOpen: (moduleId: string) => void;
}) {
  if (loading) return <Skeleton className="h-64 w-full" />;
  if (error) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm">
          <p className="text-foreground">В этом сезоне ещё нет ни одной системы.</p>
          <p className="mt-2 text-muted-foreground">
            Система собирается один раз — например «Озимая пшеница, интенсивная защита»: четыре
            обработки, препараты и нормы. Дальше её вставляют в техкарты, а не переписывают в
            каждой заново.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Культура</TableHead>
                <TableHead className="text-right">Обработок</TableHead>
                <TableHead className="text-right">Стоимость</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Где используется</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <button
                      type="button"
                      className="text-left font-medium text-foreground underline-offset-4 hover:underline"
                      onClick={() => onOpen(m.id)}
                    >
                      {m.name}
                    </button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {MODULE_KIND_SHORT[m.kind] ?? 'Система'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {m.crop_id
                      ? (cropNames[m.crop_id] ?? 'культура без названия в справочнике')
                      : 'любая культура'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{m.operations_count}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPerHa(m.cost?.per_ha ?? null)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.status === 'ready' ? 'primary' : 'outline'}>
                      {MODULE_STATUS_TITLES[m.status] ?? 'Черновик'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatUsage(m.used_in_cards_count)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
